import { memo, useCallback, useState } from 'react';
import { Spinner } from '@nextui-org/react';
import type { Assessment, ActionType } from '../types';
import { formatDate } from '../utils/formatters';
import { RiskBadge } from './RiskBadge';
import { StatusLabel } from './StatusLabel';
import { ActionsDropdown } from './ActionsDropdown';
import { BusinessCriticalityLabel } from './BusinessCriticalityLabel';

interface ProductRowProps {
  assessment: Assessment;
  vendorName: string;
  isLast?: boolean;
  onAction: (action: ActionType, assessment: Assessment) => void;
}

export const ProductRow = memo(function ProductRow({
  assessment,
  vendorName,
  isLast = false,
  onAction,
}: ProductRowProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const productName =
    assessment.multi_file_vendor_assessment.product_name || 'No Products Selected';
  const risk = assessment.risk_level;

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, [role="menu"], [role="menuitem"]')) {
        return;
      }
      setIsNavigating(true);
      onAction('view_assessment', assessment);
    },
    [onAction, assessment]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, [role="menu"], [role="menuitem"]')) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsNavigating(true);
        onAction('view_assessment', assessment);
      }
    },
    [onAction, assessment]
  );

  return (
    <div
      role="row"
      aria-level={2}
      tabIndex={isNavigating ? -1 : 0}
      onClick={isNavigating ? undefined : handleRowClick}
      onKeyDown={isNavigating ? undefined : handleKeyDown}
      aria-busy={isNavigating}
      className={`flex flex-row items-center px-3 pr-4 py-3 gap-3 h-11 bg-[#F8FAFC] transition-colors ${
        isLast ? '' : 'border-b border-[#E2E8F0]'
      } ${isNavigating ? 'opacity-70 cursor-wait' : 'cursor-pointer hover:bg-[#EFF6FF]'}`}
    >
      {/* Indent spacer - where chevron would be (hidden for child rows) */}
      <div className="w-5 h-5 flex-shrink-0" />

      {/* Columns container */}
      <div className="flex flex-row items-center justify-between flex-1 gap-4">
        {/* Product Name Column (240px) */}
        <div className="flex items-center gap-2 w-[240px]">
          {/* Logo spacer - hidden for products, maintains alignment with vendor rows */}
          <div className="w-5 h-5 flex-shrink-0" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158] truncate">
              {productName}
            </span>
          </div>
        </div>

        {/* Risk Column (160px) */}
        <div className="flex items-center w-[160px]">
          <RiskBadge risk={risk} />
        </div>

        {/* Business Criticality Column (180px) */}
        <div className="flex items-center w-[180px]">
          <BusinessCriticalityLabel criticality={assessment.business_criticality} />
        </div>

        {/* Status Column (160px) */}
        <div className="flex items-center w-[160px]">
          <StatusLabel status={assessment.status} />
        </div>

        {/* Last Assessed Column (160px) */}
        <div className="w-[160px]">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158]">
            {formatDate(assessment.updated_at)}
          </span>
        </div>

        {/* Actions Column (20px) */}
        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
          {isNavigating ? (
            <Spinner size="sm" color="primary" />
          ) : (
            <ActionsDropdown
              assessment={assessment}
              vendorName={vendorName}
              onAction={onAction}
            />
          )}
        </div>
      </div>
    </div>
  );
});
